// components/viewport/ViewportEnergyBackground.tsx
import React, { useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { createStarfieldTiles, runStarfield, VIEWPORT_STARFIELD } from '@/components/viewport/starfield';

interface ViewportEnergyBackgroundProps {
  className?: string;
}

const parseCssNumber = (value: string | null | undefined, fallback: number): number => {
  if (!value) return fallback;
  const parsed = Number.parseFloat(value.trim().replace('px', ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const ViewportEnergyBackground = React.memo(({ className }: ViewportEnergyBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const layerTilesRef = useRef<HTMLCanvasElement[]>([]);
  const [viewportSize, setViewportSize] = useState({ width: 1, height: 1 });

  useLayoutEffect(() => {
    layerTilesRef.current = createStarfieldTiles(VIEWPORT_STARFIELD);
  }, []);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const container = canvas?.parentElement;
    if (!canvas || !container) return;

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      setViewportSize({
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height)),
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const container = canvas?.parentElement;
    if (!canvas || !container) return;

    return runStarfield({
      canvas,
      config: VIEWPORT_STARFIELD,
      tiles: () => layerTilesRef.current,
      width: viewportSize.width,
      height: viewportSize.height,
      readViewportState: () => {
        const style = container.style;
        return {
          panOffset: {
            x: parseCssNumber(style.getPropertyValue('--viewport-live-pan-x'), 0),
            y: parseCssNumber(style.getPropertyValue('--viewport-live-pan-y'), 0),
          },
          zoomLevel: Math.max(0.0001, parseCssNumber(style.getPropertyValue('--viewport-live-zoom'), 1)),
        };
      },
    });
  }, [viewportSize.height, viewportSize.width]);

  return (
    <canvas
      ref={canvasRef}
      data-infinite-viewport-background="true"
      className={clsx('absolute inset-0 pointer-events-none bg-black', className)}
    />
  );
});
ViewportEnergyBackground.displayName = 'ViewportEnergyBackground';
